import { useState, useEffect, useRef, } from 'react';
import { useForm, Controller, } from 'react-hook-form'
import styled from 'styled-components';
import Select from 'react-select';
import masks from 'helpers/masks';
import fieldValidations from 'helpers/fieldValidations';
import regex from 'helpers/regex';
import colors from 'theme/colors';
import breakpoints from 'theme/breakpoints';
import Fieldset from 'components/_UI/Fieldset'
import Loader from 'components/_UI/Loader';
import { useUser } from 'context/user/User';
import financialApi from 'api/requests/financial';
import eventsApi from 'api/requests/events';
import BankAddressInfo from './BankAddressInfo';
import { Check, User, CreditCard, MapPin, ChevronRight, ChevronLeft, Trash2, Save, AlertCircle } from 'react-feather';

const steps = [
    { number: 1, label: 'Titular', icon: User },
    { number: 2, label: 'Conta', icon: CreditCard },
    { number: 3, label: 'Endereço', icon: MapPin },
];

const bankSelectStyles = {
    control: (base, state) => ({
        ...base,
        background: colors.night + '08',
        borderColor: state.isFocused ? colors.occasionalPurple : colors.night + '22',
        borderRadius: '10px',
        padding: '4px 4px',
        fontSize: '14px',
        boxShadow: state.isFocused ? `0 0 0 3px ${colors.occasionalPurple}18` : 'none',
        transition: 'all 0.15s ease',
        '&:hover': {
            borderColor: colors.occasionalPurple + '66',
        },
    }),
    menu: (base) => ({
        ...base,
        borderRadius: '10px',
        overflow: 'hidden',
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        border: `1px solid ${colors.night}11`,
        zIndex: 10,
    }),
    option: (base, state) => ({
        ...base,
        fontSize: '14px',
        padding: '10px 14px',
        background: state.isSelected
            ? colors.occasionalPurple
            : state.isFocused ? colors.occasionalPurple + '0D' : 'transparent',
        color: state.isSelected ? '#fff' : colors.nightDark,
        cursor: 'pointer',
        '&:active': {
            background: colors.occasionalPurple + '22',
        },
    }),
    placeholder: (base) => ({
        ...base,
        color: colors.night + '88',
        fontSize: '14px',
    }),
    singleValue: (base) => ({
        ...base,
        color: colors.nightDark,
        fontSize: '14px',
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base, state) => ({
        ...base,
        color: state.isFocused ? colors.occasionalPurple : colors.night + '88',
        transition: 'color 0.15s ease',
    }),
};

export default function BankForm({ visible, toast, onClose }) {
    const {
        register,
        unregister,
        setValue,
        getValues,
        setError,
        clearErrors,
        handleSubmit,
        control,
        watch,
        reset,
        trigger,
        formState,
        formState: { isDirty, isSubmitting, errors, },
    } = useForm({ mode: 'onChange', });

    const { user, setUser } = useUser();
    const [currentStep, setCurrentStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [accountId, setAccountId] = useState(null);
    const [address, setAddress] = useState({});
    const [banks, setBanks] = useState([]);
    const [cnpj, setCnpj] = useState('');
    const [isCpf, setIsCpf] = useState(true);
    const [isCurrentAccount, setIsCurrentAccount] = useState(true);
    const [isEditing, setIsEditing] = useState(false)
    const [isValid, setIsValid] = useState(false)
    const [requestError, setRequestError] = useState(false);
    const [hasActiveEvents, setHasActiveEvents] = useState(false);
    const resultError = useRef(null);

    const watchCep = watch('cep')

    const step1Fields = isCpf ? ['holder', 'cpf'] : ['holder', 'cpf', 'cnpj'];
    const step2Fields = ['bank', 'agency', 'account', 'digit'];

    useEffect(() => {
        setIsValid(Object.keys(errors).length === 0)
    }, [formState])

    function changeRadio() {
        setValue('cpf', getValues('cpf'), { shouldDirty: true })
    }

    async function goToNext() {
        const fieldsToValidate = currentStep === 1 ? step1Fields : step2Fields;
        const valid = await trigger(fieldsToValidate);
        if (valid) {
            setCurrentStep(prev => Math.min(prev + 1, 3));
        }
    }

    function goBack() {
        setCurrentStep(prev => Math.max(prev - 1, 1));
    }

    async function getBanks() {
        try {
            const { data } = await financialApi.getBanks();
            const banks = data.data.map((bank) => ({
                label: `${bank.code} - ${bank.name}`,
                value: bank.id
            }));

            return banks;
        } catch (error) {
            // log error
        }
    }

    async function getAccount() {
        try {
            const response = await financialApi.getBankAccount();

            return response.data;
        } catch (error) {
            // log error
        }
    }

    async function deleteBankAccount() {
        try {
            setLoading(true);
            setRequestError(false)
            await financialApi.deleteBankAccount(accountId);

            resetForm();
            setAccountId(null);
            onClose();

            toast.success('Conta deletada com sucesso');
        } catch (error) {
            // log error
            setRequestError('Ocorreu um erro inesperado!')
        } finally {
            setLoading(false);
            setTimeout(() => {
                resultError?.current?.scrollIntoView({ behavior: 'smooth' });
            }, 400);
        }
    }

    async function editBankAccount(data) {
        const { holder, digit, account, agency, agencyDigit, cpf, bank } = data;
        try {
            setLoading(true);
            setIsEditing(true);
            setRequestError(false);
            const payload = {
                holder: holder,
                agency: agency.replaceAll(regex.nonDigit, ''),
                agency_digit: agencyDigit || '',
                account: `${account}${digit}`,
                account_type: isCurrentAccount ? 'corrente' : 'poupanca',
                cpf_cnpj: isCpf ? 'cpf' : 'cnpj',
                cpf: cpf.replaceAll(regex.nonDigit, ''),
                bank_id: bank.value,
                address: {
                    postalcode: data.cep,
                    number: String(data.addressNumber),
                }
            };

            if (!isCpf) {
                payload.cnpj = data.cnpj.replaceAll(regex.nonDigit, '');
            }

            await financialApi.editBankAccount(accountId, payload);

            onClose();

            toast.success('Conta editada com sucesso');
        } catch (error) {
            // log error
            setRequestError('Ocorreu um erro inesperado!')
        } finally {
            setLoading(false);
            setTimeout(() => {
                resultError?.current?.scrollIntoView({ behavior: 'smooth' });
            }, 400);
        }
    }

    async function createBankAccount(data) {
        const { holder, digit, account, agency, agencyDigit, cpf, bank, cep, addressNumber } = data;
        try {
            setLoading(true);
            setRequestError(false);
            const payload = {
                holder: holder,
                agency: agency.replaceAll(regex.nonDigit, ''),
                agency_digit: agencyDigit || '',
                account: `${account}${digit}`,
                account_type: isCurrentAccount ? 'corrente' : 'poupanca',
                cpf_cnpj: isCpf ? 'cpf' : 'cnpj',
                cpf: cpf.replaceAll(regex.nonDigit, ''),
                bank_id: bank.value,
                address: {
                    postalcode: cep,
                    number: String(addressNumber),
                }
            };
            if (!isCpf) {
                payload.cnpj = data.cnpj.replaceAll(regex.nonDigit, '');
            }

            const { data: accounting } = await financialApi.registerBankAccount(payload);

            setAccountId(accounting.id);
            refetchUser();

            onClose();

            toast.success('Conta criada com sucesso');
        } catch (error) {
            setRequestError('Ocorreu um erro inesperado!');
        } finally {
            setLoading(false);
            setTimeout(() => {
                resultError?.current?.scrollIntoView({ behavior: 'smooth' });
            }, 400);
        }
    }

    function refetchUser() {
        setUser({
            ...user,
            has_bank_account: true
        });
    }

    function resetForm() {
        reset({
            holder: '',
            cpf: '',
            bank: null,
            agency: '',
            agencyDigit: '',
            account: '',
            digit: '',
            cnpj: '',
            cep: '',
            addressNumber: '',
            addressDistrict: '',
            addressStreet: '',
        })
        setAccountId(null)
    }

    async function setInitialData() {
        try {
            setLoading(true);
            const banks = await getBanks();
            setBanks(banks);

            const { data: accounting } = await getAccount();
            const { data: myEvents } = await eventsApi.myEvents();

            if (myEvents?.data?.now?.total > 0 || myEvents?.data?.next?.total > 0) {
                setHasActiveEvents(true);
            }

            if (!accounting || !accounting.id) {
                return;
            }

            setIsEditing(true);

            const account = accounting.account.slice(0, -1);
            const digit = accounting.account.slice(-1);

            setAccountId(accounting.id);
            setAddress(accounting.address);
            setValue('holder', accounting.holder);
            setValue('account', account);
            setValue('digit', digit);
            setValue('agency', accounting.agency);
            setValue('agencyDigit', accounting.agency_digit || '');
            setValue('cpf', accounting.cpf);

            if (accounting.cnpj) {
                setValue('cnpj', accounting.cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5"));
            }

            setIsCpf(accounting.cpf_cnpj === 'cpf');
            setIsCurrentAccount(accounting.account_type === 'corrente');

            const userBank = banks.find(bank => bank.value === accounting.bank_id);
            setValue('bank', userBank);

            setValue('cep', accounting.address.street.postalcode);
            setValue('addressNumber', accounting.address.number);
            setValue('addressDistrict', accounting.address.street.district);
            setValue('addressStreet', accounting.address.street.name);
        } catch (error) {
            console.log(error)
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (visible) {
            setCurrentStep(1);
            setInitialData();
        }
    }, [visible]);

    useEffect(() => {
        if (isCpf) {
            setCnpj(getValues('cnpj'))
            unregister('cnpj')
        } else {
            register('cnpj')

            if (!getValues('cnpj')) {
                setValue('cnpj', cnpj)
            }
        }
    }, [isCpf])

    const canSubmit = isDirty && isValid && !isSubmitting;

    return (
        <ModalWrapper>
            <ModalHeader>
                <HeaderLeft>
                    <TitleText>Conta bancária</TitleText>
                    {accountId && <EditingBadge>Editando</EditingBadge>}
                </HeaderLeft>
                {accountId && !hasActiveEvents && (
                    <DeleteButton
                        onClick={deleteBankAccount}
                        disabled={isSubmitting}
                        title='Deletar conta'
                    >
                        <Trash2 size={18} />
                        <span>Deletar</span>
                    </DeleteButton>
                )}
            </ModalHeader>

            {loading ?
                <Loader /> :
                <>
                    <StepperContainer>
                        {steps.map((step, index) => {
                            const StepIcon = step.icon;
                            const isActive = currentStep === step.number;
                            const isCompleted = currentStep > step.number;

                            return (
                                <StepperItem key={step.number}>
                                    <StepCircle
                                        active={isActive}
                                        completed={isCompleted}
                                        onClick={() => {
                                            if (isCompleted) setCurrentStep(step.number);
                                        }}
                                        clickable={isCompleted}
                                    >
                                        {isCompleted
                                            ? <Check size={14} color='#fff' />
                                            : <StepIcon size={14} color={isActive ? '#fff' : colors.night + '88'} />
                                        }
                                    </StepCircle>
                                    <StepLabel active={isActive} completed={isCompleted}>
                                        {step.label}
                                    </StepLabel>
                                    {index < steps.length - 1 && (
                                        <StepLine completed={currentStep > step.number} />
                                    )}
                                </StepperItem>
                            );
                        })}
                    </StepperContainer>

                    <ModernFormArea>
                        {/* Step 1 - Titular */}
                        {currentStep === 1 && (
                            <StepContent>
                                <StepDescription>
                                    Informe os dados do titular da conta bancária.
                                </StepDescription>

                                <FieldGrid columns={2}>
                                    <FieldWrapper>
                                        <Fieldset
                                            placeholder='Nome do titular'
                                            label='Nome do titular'
                                            layout='squired'
                                            name='holder'
                                            register={register}
                                            validations={fieldValidations.generic('Titular')}
                                        />
                                        {errors?.holder?.message && (
                                            <FieldError>
                                                <AlertCircle size={13} />
                                                {errors.holder?.message}
                                            </FieldError>
                                        )}
                                    </FieldWrapper>

                                    <FieldWrapper>
                                        <Fieldset
                                            placeholder='CPF'
                                            label='CPF'
                                            layout='squired'
                                            name='cpf'
                                            mask={masks.cpf}
                                            register={register}
                                            validations={fieldValidations.cpf('CPF')}
                                        />
                                        {errors?.cpf?.message && (
                                            <FieldError>
                                                <AlertCircle size={13} />
                                                {errors.cpf?.message}
                                            </FieldError>
                                        )}
                                    </FieldWrapper>
                                </FieldGrid>

                                <SegmentedContainer>
                                    <SegmentedLabel>Tipo de entidade</SegmentedLabel>
                                    <SegmentedRow>
                                        <SegmentedButton
                                            active={isCpf}
                                            onClick={() => { setIsCpf(true); changeRadio() }}
                                        >
                                            Pessoa Física
                                        </SegmentedButton>
                                        <SegmentedButton
                                            active={!isCpf}
                                            onClick={() => { setIsCpf(false); changeRadio() }}
                                        >
                                            Pessoa Jurídica
                                        </SegmentedButton>
                                    </SegmentedRow>
                                </SegmentedContainer>

                                {!isCpf && (
                                    <FieldGrid columns={1} style={{ marginTop: 12 }}>
                                        <FieldWrapper half>
                                            <Fieldset
                                                placeholder='CNPJ'
                                                label='CNPJ'
                                                layout='squired'
                                                name='cnpj'
                                                mask={masks.cnpj}
                                                register={register}
                                                validations={fieldValidations.cnpj('CNPJ', !isCpf)}
                                            />
                                            {errors?.cnpj?.message && (
                                                <FieldError>
                                                    <AlertCircle size={13} />
                                                    {errors.cnpj?.message}
                                                </FieldError>
                                            )}
                                        </FieldWrapper>
                                    </FieldGrid>
                                )}
                            </StepContent>
                        )}

                        {/* Step 2 - Conta */}
                        {currentStep === 2 && (
                            <StepContent>
                                <StepDescription>
                                    Selecione seu banco e informe os dados da conta.
                                </StepDescription>

                                <FieldGrid columns={1}>
                                    <FieldWrapper>
                                        <SelectLabel>Banco</SelectLabel>
                                        <Controller
                                            control={control}
                                            name='bank'
                                            rules={{ required: true }}
                                            render={({ field }) => (
                                                <Select
                                                    placeholder='Selecione seu banco...'
                                                    options={banks}
                                                    styles={bankSelectStyles}
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    onBlur={field.onBlur}
                                                    ref={field.ref}
                                                />
                                            )}
                                        />
                                        {errors?.bank && (
                                            <FieldError>
                                                <AlertCircle size={13} />
                                                Selecione um banco
                                            </FieldError>
                                        )}
                                    </FieldWrapper>
                                </FieldGrid>

                                <FieldGrid columns={4}>
                                    <FieldWrapper>
                                        <Fieldset
                                            placeholder='Agência'
                                            label='Agência'
                                            layout='squired'
                                            name='agency'
                                            register={register}
                                            validations={fieldValidations.bankAgency()}
                                        />
                                        {errors?.agency?.message && (
                                            <FieldError>
                                                <AlertCircle size={13} />
                                                {errors.agency?.message}
                                            </FieldError>
                                        )}
                                    </FieldWrapper>
                                    <FieldWrapper>
                                        <Fieldset
                                            placeholder='Dígito'
                                            label='Dígito Ag.'
                                            layout='squired'
                                            name='agencyDigit'
                                            register={register}
                                            validations={{ maxLength: { value: 1, message: 'Máximo 1 dígito' } }}
                                            maxLength={1}
                                        />
                                    </FieldWrapper>
                                    <FieldWrapper>
                                        <Fieldset
                                            placeholder='Conta'
                                            label='Conta'
                                            layout='squired'
                                            name='account'
                                            maskPlaceholder=''
                                            masklabel=''
                                            register={register}
                                            validations={fieldValidations.bankAccount()}
                                        />
                                        {errors?.account?.message && (
                                            <FieldError>
                                                <AlertCircle size={13} />
                                                {errors.account?.message}
                                            </FieldError>
                                        )}
                                    </FieldWrapper>
                                    <FieldWrapper>
                                        <Fieldset
                                            placeholder='Dígito'
                                            label='Dígito'
                                            layout='squired'
                                            name='digit'
                                            register={register}
                                            validations={fieldValidations.bankDigit()}
                                        />
                                        {errors?.digit?.message && (
                                            <FieldError>
                                                <AlertCircle size={13} />
                                                {errors.digit?.message}
                                            </FieldError>
                                        )}
                                    </FieldWrapper>
                                </FieldGrid>

                                <SegmentedContainer>
                                    <SegmentedLabel>Tipo de conta</SegmentedLabel>
                                    <SegmentedRow>
                                        <SegmentedButton
                                            active={isCurrentAccount}
                                            onClick={() => { setIsCurrentAccount(true); changeRadio() }}
                                        >
                                            Corrente
                                        </SegmentedButton>
                                        <SegmentedButton
                                            active={!isCurrentAccount}
                                            onClick={() => { setIsCurrentAccount(false); changeRadio() }}
                                        >
                                            Poupança
                                        </SegmentedButton>
                                    </SegmentedRow>
                                </SegmentedContainer>
                            </StepContent>
                        )}

                        {/* Step 3 - Endereço */}
                        {currentStep === 3 && (
                            <StepContent>
                                <StepDescription>
                                    Informe o endereço vinculado à conta bancária.
                                </StepDescription>

                                <BankAddressInfo
                                    register={register}
                                    setValue={setValue}
                                    getValues={getValues}
                                    setError={setError}
                                    clearErrors={clearErrors}
                                    errors={errors}
                                    isEditing={isEditing}
                                    setIsEditing={setIsEditing}
                                    watchCep={watchCep}
                                />

                                {requestError && (
                                    <RequestError ref={resultError}>
                                        <AlertCircle size={16} />
                                        {requestError}
                                    </RequestError>
                                )}
                            </StepContent>
                        )}
                    </ModernFormArea>

                    <Footer>
                        <FooterLeft>
                            {currentStep > 1 && (
                                <BackButton onClick={goBack}>
                                    <ChevronLeft size={18} />
                                    Voltar
                                </BackButton>
                            )}
                        </FooterLeft>

                        <FooterRight>
                            {currentStep < 3 ? (
                                <NextButton onClick={goToNext}>
                                    Próximo
                                    <ChevronRight size={18} />
                                </NextButton>
                            ) : (
                                <SubmitButton
                                    disabled={!canSubmit}
                                    onClick={accountId ? handleSubmit(editBankAccount) : handleSubmit(createBankAccount)}
                                >
                                    <Save size={18} />
                                    {accountId ? 'Atualizar conta' : 'Criar conta'}
                                </SubmitButton>
                            )}
                        </FooterRight>
                    </Footer>
                </>
            }
        </ModalWrapper>
    )
}

/* ============================
   Layout
   ============================ */

const ModalWrapper = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
`;

const ModalHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 1px solid ${colors.night}11;
`;

const HeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const TitleText = styled.h2`
    font-size: 20px;
    font-weight: 700;
    color: ${colors.occasionalPurple};
    margin: 0;
`;

const EditingBadge = styled.span`
    font-size: 11px;
    font-weight: 600;
    color: ${colors.occasionalPurple};
    background: ${colors.occasionalPurple}14;
    border: 1px solid ${colors.occasionalPurple}33;
    padding: 3px 10px;
    border-radius: 20px;
`;

const DeleteButton = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border: 1px solid ${colors.danger}44;
    border-radius: 8px;
    background: transparent;
    color: ${colors.danger};
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover:not(:disabled) {
        background: ${colors.danger}0D;
        border-color: ${colors.danger};
    }

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }

    & > span {
        @media screen and (max-width: ${breakpoints.sm}) {
            display: none;
        }
    }
`;

/* ============================
   Stepper
   ============================ */

const StepperContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 28px;
    padding: 0 10px;
`;

const StepperItem = styled.div`
    display: flex;
    align-items: center;
`;

const StepCircle = styled.div`
    width: 34px;
    height: 34px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.2s ease;
    cursor: ${({ clickable }) => clickable ? 'pointer' : 'default'};
    background: ${({ active, completed }) =>
        active ? colors.occasionalPurple
        : completed ? colors.occasionalPurple
        : colors.night + '0D'};
    border: 2px solid ${({ active, completed }) =>
        active || completed ? colors.occasionalPurple : colors.night + '22'};

    ${({ clickable }) => clickable && `
        &:hover {
            transform: scale(1.08);
        }
    `}
`;

const StepLabel = styled.span`
    font-size: 13px;
    font-weight: ${({ active }) => active ? '700' : '400'};
    margin-left: 6px;
    white-space: nowrap;
    color: ${({ active, completed }) =>
        active || completed ? colors.occasionalPurple : colors.night + '88'};
`;

const StepLine = styled.div`
    width: 40px;
    height: 2px;
    margin: 0 10px;
    flex-shrink: 0;
    border-radius: 1px;
    background: ${({ completed }) => completed ? colors.occasionalPurple : colors.night + '18'};
    transition: background 0.2s ease;

    @media screen and (max-width: ${breakpoints.sm}) {
        width: 20px;
        margin: 0 6px;
    }
`;

/* ============================
   Modern form area
   Overrides all nested Fieldset
   input/label styles via cascade
   ============================ */

const ModernFormArea = styled.div`
    flex: 1;
    width: 100%;

    /* Override label styles */
    label {
        font-size: 13px;
        font-weight: 600;
        color: ${colors.nightDark};
        margin-bottom: 4px;
        display: block;
    }

    /* Override squired inputs but not react-select internals */
    input:not([class*="css-"]):not([id*="react-select"]) {
        background: ${colors.night}08 !important;
        border: 1.5px solid ${colors.night}22 !important;
        border-radius: 10px !important;
        padding: 12px 14px !important;
        font-size: 14px !important;
        margin-top: 6px !important;
        transition: all 0.15s ease !important;
        color: ${colors.nightDark} !important;

        &::placeholder {
            color: ${colors.night}77 !important;
            font-weight: 400 !important;
        }

        &:focus {
            border-color: ${colors.occasionalPurple} !important;
            box-shadow: 0 0 0 3px ${colors.occasionalPurple}18 !important;
            background: #fff !important;
        }
    }
`;

const StepContent = styled.div`
    width: 100%;
`;

const StepDescription = styled.p`
    font-size: 14px;
    color: ${colors.night};
    margin: 0 0 20px 0;
    line-height: 1.5;
`;

/* ============================
   Field grid & wrappers
   ============================ */

const FieldGrid = styled.div`
    display: grid;
    gap: 16px;
    grid-template-columns: 1fr;
    margin-bottom: 8px;

    @media screen and (min-width: ${breakpoints.md}) {
        grid-template-columns: repeat(${({ columns }) => columns || 1}, 1fr);
    }
`;

const FieldWrapper = styled.div`
    width: 100%;

    ${({ half }) => half && `
        @media screen and (min-width: ${breakpoints.md}) {
            max-width: 50%;
        }
    `}
`;

const SelectLabel = styled.label`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin-bottom: 6px;
    display: block;
`;

const FieldError = styled.span`
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    font-weight: 500;
    color: ${colors.danger};
    margin-top: 6px;
    padding: 4px 8px;
    background: ${colors.danger}0A;
    border-radius: 6px;
    line-height: 1.4;
`;

const RequestError = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-top: 16px;
    padding: 12px 16px;
    border-radius: 10px;
    background: ${colors.danger}0D;
    border: 1px solid ${colors.danger}22;
    color: ${colors.danger};
    font-size: 14px;
    font-weight: 500;
`;

/* ============================
   Segmented control
   ============================ */

const SegmentedContainer = styled.div`
    margin-top: 20px;
    width: 100%;

    @media screen and (min-width: ${breakpoints.md}) {
        width: 50%;
    }
`;

const SegmentedLabel = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin-bottom: 8px;
    display: block;
`;

const SegmentedRow = styled.div`
    display: flex;
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid ${colors.night}18;
    background: ${colors.night}08;
`;

const SegmentedButton = styled.button`
    flex: 1;
    padding: 10px 16px;
    border: none;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    transition: all 0.15s ease;
    background: ${({ active }) => active ? colors.occasionalPurple : 'transparent'};
    color: ${({ active }) => active ? '#fff' : colors.night + 'AA'};

    &:hover {
        background: ${({ active }) => active ? colors.occasionalPurple : colors.occasionalPurple + '0D'};
        color: ${({ active }) => active ? '#fff' : colors.occasionalPurple};
    }
`;

/* ============================
   Footer / navigation
   ============================ */

const Footer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 28px;
    padding-top: 18px;
    border-top: 1px solid ${colors.night}11;
    width: 100%;
`;

const FooterLeft = styled.div`
    display: flex;
`;

const FooterRight = styled.div`
    display: flex;
    margin-left: auto;
`;

const BackButton = styled.button`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 10px 18px;
    border: 1px solid ${colors.night}22;
    border-radius: 10px;
    background: transparent;
    color: ${colors.night};
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
        background: ${colors.night}08;
        border-color: ${colors.night}44;
    }
`;

const NextButton = styled.button`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 10px 24px;
    border: none;
    border-radius: 10px;
    background: ${colors.occasionalPurple};
    color: #fff;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
        background: ${colors.secondaryPurple};
        transform: translateX(2px);
    }
`;

const SubmitButton = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 24px;
    border: none;
    border-radius: 10px;
    background: ${({ disabled }) => disabled ? colors.night + '18' : colors.occasionalPurple};
    color: ${({ disabled }) => disabled ? colors.night + '66' : '#fff'};
    font-size: 14px;
    font-weight: 700;
    cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
    transition: all 0.15s ease;

    &:hover:not(:disabled) {
        background: ${colors.secondaryPurple};
    }
`;
